import { type RovoContentBridgeAPI, type client } from '@canvas/rovo-content-bridge-api';

import { forwardWheelEventToBridge } from './wheel-gestures/bridge-utilities.ts';
import { PanHandler } from './wheel-gestures/pan-handler.ts';
import { ScrollEndHandler } from './wheel-gestures/scroll-end-handler.ts';
import { WheelGestureCoordinator } from './wheel-gestures/wheel-gesture-coordinator.ts';
import { ZoomHandler } from './wheel-gestures/zoom-handler.ts';

type ForwardWheelEventsParams = {
  bridge: RovoContentBridgeAPI;
  localClientId: ReturnType<typeof client>;
  externalClientId: ReturnType<typeof client>;
};

/**
 * Sets up wheel event forwarding from the database iframe to the parent via the bridge.
 * The key decisions this function makes is to inform parent how to handle forwarded
 * events -- either zoom, pan or scroll.
 */
export function setupWheelEventForwarding({
  bridge,
  localClientId,
  externalClientId,
}: ForwardWheelEventsParams): () => void {
  // Create closure functions to pass as props to wheel gesture handlers
  const forwardToBridge = (event: WheelEvent) =>
    forwardWheelEventToBridge(event, bridge, localClientId, externalClientId);
  const onGestureEnd = () => coordinator.unlockHandler();

  // Create gesture coordinator to manage gesture state with ordered list of handlers
  const coordinator = new WheelGestureCoordinator([
    new ZoomHandler(forwardToBridge), // Forwards zoom gestures to container (e.g. WB/Pages)
    new ScrollEndHandler(onGestureEnd), // Informs coordinator when current scroll gesture completes
    new PanHandler(forwardToBridge, onGestureEnd), // Forwards pan gestures to container when no scrollable content
  ]);

  // Handle wheel events and delegate to gesture coordinator
  const handleWheel = (event: WheelEvent) => {
    coordinator.handleWheel(event);
  };

  const eventOptions = {
    passive: false,
    capture: true,
  };

  document.addEventListener('wheel', handleWheel, eventOptions);

  return () => {
    document.removeEventListener('wheel', handleWheel, eventOptions);
    coordinator.cleanup();
  };
}
